/**
 * WP12d — Bąbelki (MgBubbles): pop bąbelków z zabawkami, 90 s, stałe tempo.
 * Nagrody WYŁĄCZNIE przez core/RunClient. Brak karzącego game-over.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { runClient } from '../../core/RunClient.js';
import {
  BUBBLES_DURATION_MS,
  BUBBLES_GAME_ID,
  BUBBLES_SPAWN_INTERVAL_MS,
  bubbleRadius,
  buildRunStats,
  createBubblesState,
  finishRound,
  formatTimerPl,
  isGoodGame,
  isTimeUp,
  registerMissTap,
  registerPop,
  remainingMs,
  shouldSpawn,
  spawnPosition,
  tickElapsed,
  toyEmojiAt,
  type BubblesState,
} from './bubblesLogic.js';

export type MgBubblesSceneData = {
  returnTo?: string;
};

type LiveBubble = {
  id: number;
  container: Phaser.GameObjects.Container;
  radius: number;
};

export class MgBubblesScene extends BaseScene {
  static readonly KEY = 'MgBubbles';

  private state: BubblesState = createBubblesState(0);
  private runId: string | null = null;
  private returnTo = 'School';
  private settling = false;
  private bubbleSeq = 0;
  private lastSpawnMs = 0;
  private bubbles: LiveBubble[] = [];
  private hudTimer: Phaser.GameObjects.Text | null = null;
  private hudStars: Phaser.GameObjects.Text | null = null;
  private hudHint: Phaser.GameObjects.Text | null = null;
  private playArea: Phaser.GameObjects.Zone | null = null;
  private spaceKey: Phaser.Input.Keyboard.Key | null = null;

  constructor() {
    super(MgBubblesScene.KEY);
  }

  init(data: MgBubblesSceneData = {}): void {
    this.returnTo = data.returnTo ?? 'School';
    this.runId = null;
    this.settling = false;
    this.bubbleSeq = 0;
    this.lastSpawnMs = 0;
    this.bubbles = [];
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xbde0fe);
    const now = this.time.now;
    this.state = createBubblesState(now, BUBBLES_DURATION_MS);
    this.lastSpawnMs = now - BUBBLES_SPAWN_INTERVAL_MS; // pierwszy bąbelek zaraz

    this.drawSkyDecor();
    this.buildHud();
    this.bindInput();
    void this.beginRun();

    // pierwsze spawny od razu
    this.spawnBubble();
    this.spawnBubble();
  }

  update(_time: number, _delta: number): void {
    if (this.settling || this.state.finished) return;

    const now = this.time.now;
    this.state = tickElapsed(this.state, now);
    this.refreshHud();

    if (isTimeUp(this.state)) {
      void this.endRound('time');
      return;
    }

    if (shouldSpawn(this.state, this.lastSpawnMs, now, BUBBLES_SPAWN_INTERVAL_MS)) {
      this.lastSpawnMs = now;
      this.spawnBubble();
    }

    this.floatBubbles(_delta);
  }

  private async beginRun(): Promise<void> {
    try {
      const { runId } = await runClient.startRun(BUBBLES_GAME_ID);
      this.runId = runId;
    } catch {
      // offline / stub — gra lokalna bez zł (§7.5)
      this.runId = null;
    }
  }

  private drawSkyDecor(): void {
    // delikatne chmurki proceduralne
    for (let i = 0; i < 5; i++) {
      const x = 120 + i * 240;
      const y = 80 + (i % 2) * 40;
      const cloud = this.add.graphics().setDepth(1);
      cloud.fillStyle(0xffffff, 0.45);
      cloud.fillCircle(x, y, 36);
      cloud.fillCircle(x + 28, y + 6, 28);
      cloud.fillCircle(x - 24, y + 8, 24);
    }
  }

  private buildHud(): void {
    // tło interaktywne POD bąbelkami i HUD — puste tapy bez kary (brak fail-state)
    this.playArea = this.add
      .zone(this.gameW / 2, this.gameH / 2, this.gameW, this.gameH)
      .setInteractive()
      .setDepth(0);
    this.playArea.on('pointerdown', () => {
      if (this.settling || this.state.finished) return;
      this.state = registerMissTap(this.state);
    });

    this.add
      .text(this.gameW / 2, 28, 'Bąbelki', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#2d5a87',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(30);

    this.hudTimer = this.add
      .text(24, 28, '⏱ 1:30', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#2d5a87',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(30);

    this.hudStars = this.add
      .text(this.gameW - 24, 28, '⭐ 0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#2d5a87',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5)
      .setDepth(30);

    this.hudHint = this.add
      .text(this.gameW / 2, this.gameH - 28, 'Stuknij bąbelek z zabawką!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#3d6a94',
      })
      .setOrigin(0.5)
      .setDepth(30);

    const exitBtn = UiKit.button(this, {
      x: this.gameW - 100,
      y: this.gameH - 48,
      label: 'Wyjście',
      width: 160,
      height: MIN_TOUCH,
      fill: 0xffd6e7,
      onClick: () => {
        void this.endRound('exit');
      },
    });
    exitBtn.setDepth(40);
  }

  private bindInput(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    this.spaceKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.spaceKey.on('down', () => this.popNearestOrOldest());
    const enter = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
    enter.on('down', () => this.popNearestOrOldest());
  }

  private refreshHud(): void {
    if (this.hudTimer) {
      this.hudTimer.setText(`⏱ ${formatTimerPl(remainingMs(this.state))}`);
    }
    if (this.hudStars) {
      this.hudStars.setText(`⭐ ${this.state.stars}`);
    }
  }

  private spawnBubble(): void {
    if (this.state.finished || this.settling) return;

    const rng = () => Math.random();
    const radius = bubbleRadius(rng);
    const { x, y } = spawnPosition(rng, this.gameW, this.gameH, radius);
    const id = ++this.bubbleSeq;
    const colors = [0x7ec8ff, 0xa8e6ff, 0xc5f0ff, 0x9ad0ff, 0xb8e0ff];
    const fill = colors[id % colors.length]!;

    const g = this.add.graphics();
    g.fillStyle(fill, 0.55);
    g.fillCircle(0, 0, radius);
    g.lineStyle(3, 0xffffff, 0.85);
    g.strokeCircle(0, 0, radius);
    // błysk
    g.fillStyle(0xffffff, 0.55);
    g.fillCircle(-radius * 0.35, -radius * 0.35, radius * 0.22);

    // zabawka w środku (emoji) — opcjonalnie texture mg_bubble jako fallback warstwa
    const toy = this.add
      .text(0, 0, toyEmojiAt(id), {
        fontFamily: 'system-ui, Apple Color Emoji, sans-serif',
        fontSize: `${Math.floor(radius * 0.95)}px`,
      })
      .setOrigin(0.5);

    const hit = this.add
      .circle(0, 0, Math.max(radius, MIN_TOUCH / 2), 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });

    const container = this.add.container(x, y, [g, toy, hit]);
    container.setSize(radius * 2, radius * 2);
    container.setDepth(10);

    // delikatny wlot
    container.setScale(0.2);
    this.tweens.add({
      targets: container,
      scaleX: 1,
      scaleY: 1,
      duration: 220,
      ease: 'Back.easeOut',
    });

    const live: LiveBubble = { id, container, radius };
    this.bubbles.push(live);

    const pop = () => this.popBubble(live);
    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation?.();
      pop();
    });

    // auto-zniknięcie po wypłynięciu — bez kary
    this.time.delayedCall(7000, () => {
      if (!container.active) return;
      this.tweens.add({
        targets: container,
        alpha: 0,
        duration: 400,
        onComplete: () => this.removeBubble(live, false),
      });
    });
  }

  private floatBubbles(delta: number): void {
    const drift = (delta / 1000) * 28;
    for (const b of this.bubbles) {
      if (!b.container.active) continue;
      b.container.y -= drift;
      b.container.x += Math.sin(this.time.now / 400 + b.id) * 0.35;
      if (b.container.y + b.radius < 40) {
        this.removeBubble(b, false);
      }
    }
  }

  private popNearestOrOldest(): void {
    if (this.settling || this.state.finished || this.bubbles.length === 0) return;
    // najniższy (najbliżej „do złapania”) lub najstarszy
    let best = this.bubbles[0]!;
    for (const b of this.bubbles) {
      if (b.container.y > best.container.y) best = b;
    }
    this.popBubble(best);
  }

  private popBubble(live: LiveBubble): void {
    if (this.settling || this.state.finished) return;
    if (!this.bubbles.includes(live)) return;

    this.state = registerPop(this.state);
    this.refreshHud();
    this.burstFx(live.container.x, live.container.y);
    this.removeBubble(live, true);

    if (this.hudHint && isGoodGame(this.state.pops) && this.state.pops === 12) {
      this.hudHint.setText('Super! Dobra gra — dalej tak!');
    }
  }

  private removeBubble(live: LiveBubble, popped: boolean): void {
    const idx = this.bubbles.indexOf(live);
    if (idx >= 0) this.bubbles.splice(idx, 1);
    if (!live.container.active) return;
    if (popped) {
      this.tweens.add({
        targets: live.container,
        scaleX: 1.35,
        scaleY: 1.35,
        alpha: 0,
        duration: 160,
        onComplete: () => live.container.destroy(true),
      });
    } else {
      live.container.destroy(true);
    }
  }

  private burstFx(x: number, y: number): void {
    // proceduralne confetti / blaski (PLAN §20)
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const dist = 24 + Math.random() * 36;
      const c = this.add.circle(
        x,
        y,
        4 + Math.random() * 5,
        [0xffb6c1, 0xffe066, 0x9bdbff, 0xc3f584][i % 4],
        0.95,
      );
      this.tweens.add({
        targets: c,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        duration: 320 + Math.random() * 180,
        onComplete: () => c.destroy(),
      });
    }
  }

  private async endRound(_reason: 'time' | 'exit'): Promise<void> {
    if (this.settling || this.state.finished) return;
    this.settling = true;
    this.state = tickElapsed(this.state, this.time.now);
    this.state = finishRound(this.state);
    this.refreshHud();

    // wyczyść bąbelki
    for (const b of [...this.bubbles]) {
      this.removeBubble(b, false);
    }

    if (this.hudHint) {
      this.hudHint.setText(
        isGoodGame(this.state.pops)
          ? 'Brawo! Pęknięte bąbelki ⭐'
          : 'Koniec rundy — super zabawa!',
      );
    }

    const stats = buildRunStats(this.state);
    let rewardZl = 0;

    if (this.runId) {
      try {
        const result = await runClient.finishRun(this.runId, stats);
        rewardZl = result.rewardZl;
      } catch {
        // za krótka runda / sieć — zero zł, bez game-over
        rewardZl = 0;
      }
    }

    getSceneRouter().go('Reward', {
      rewardZl,
      returnTo: this.returnTo,
      stars: this.state.stars,
      score: this.state.score,
    });
  }
}

export default MgBubblesScene;
